import { ClickAndCollectCountdownUI } from "./ClickAndCollectCountdownUI";
import { StandardProps } from "../componentMap";
import { ensureIsActiveVariant } from "@/utils/product/ensureIsActive.ts";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { isBetweenDates } from "@/utils/date/isBetweenDates.ts";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";

const CLICK_AND_COLLECT_DMS_CODE = "187";

const hideIfDates: [string, string][] = [
  ["2024-12-23", "2024-12-26"],
  ["2024-12-31", "2024-12-31"],
];

const defaultConfig = {
  orderByTime: "19:00",
  collectFromTime: "10:00",
  countdownDurationHours: 3,
  hideIfWithinLastMinutes: 5,
};

export function ClickAndCollectCountdown({
  product,
  runtimeConfig,
  experiencesRawObject,
  flags,
}: Pick<
  StandardProps,
  "product" | "runtimeConfig" | "experiencesRawObject" | "flags"
>) {
  const { variant, isSelected } = useVariant(product);
  if (product.__typename !== "SimpleProduct") return null;
  const activeVariant = ensureIsActiveVariant(variant);
  const isShipFromStore = variant.availability?.shipFromStore === true;
  if (!activeVariant) return null;
  if (isShipFromStore && isSelected) return null;

  if (Array.isArray(hideIfDates)) {
    const shouldHideWithinDates = hideIfDates.some(([startTime, endTime]) =>
      isBetweenDates(startTime, endTime),
    );

    if (shouldHideWithinDates) {
      return null;
    }
  }

  const { availability } = activeVariant;

  if (
    !availability.availableToOrder ||
    availability.futureAvailability ||
    availability.preOrder
  )
    return null;

  const clickAndCollectFulfilmentOption =
    product.fulfilmentOptions.available.find(
      (option) => option.aliases?.dmsId === CLICK_AND_COLLECT_DMS_CODE,
    );

  if (!clickAndCollectFulfilmentOption) return null;

  const recordImpression = (value: string) => {
    recordImpressionByParam(
      runtimeConfig?.monetateEnvironment,
      experiencesRawObject as [],
      "pdp-scaffold-showClickAndCollectCountdown",
      value,
    );
  };

  return (
    <ClickAndCollectCountdownUI
      orderByLocalTimeToday={
        clickAndCollectFulfilmentOption.earliestFulfilment
          ?.orderByLocalTimeToday || defaultConfig.orderByTime
      }
      collectFromLocalTimeTomorrow={
        clickAndCollectFulfilmentOption.earliestFulfilment
          ?.collectFromLocalTimeTomorrow || defaultConfig.collectFromTime
      }
      countdownDurationHours={defaultConfig.countdownDurationHours}
      hideIfWithinLastMinutes={defaultConfig.hideIfWithinLastMinutes}
      recordImpression={recordImpression}
      showClickAndCollectCountdown={
        flags.showClickAndCollectCountdown.value === true
      }
    />
  );
}
